// Phone country codes: one shared list for every phone picker.
//
// Which codes a hospital offers is a per-hospital setting
// (hospital_feature_flags.phone_country_codes, edited in the sales dashboard).
// This file only knows every country's name and how many digits its mobile
// numbers have after the code.
//
// GENERATED from libphonenumber-js 1.11.20 mobile metadata (one row per calling
// code, the code's main country). Do not hand-edit lengths; regenerate instead.
// Kept as plain data so the app needs no phone library at runtime.

export interface CountryCode {
  code: string; // calling code, digits only ("91")
  iso: string; // ISO 3166 alpha-2 of the code's main country
  short: string; // what the picker shows: ISO alpha-3, except UAE / UK
  name: string;
  lengths: number[]; // valid national (after-code) mobile number lengths
}

// Offered when a hospital has no list (older backend, missing column).
export const DEFAULT_COUNTRY_CODES = ["91", "964", "974", "971"];

export const COUNTRY_CODES: CountryCode[] = [
  { code: "93", iso: "AF", short: "AFG", name: "Afghanistan", lengths: [9] },
  { code: "355", iso: "AL", short: "ALB", name: "Albania", lengths: [9] },
  { code: "213", iso: "DZ", short: "DZA", name: "Algeria", lengths: [9] },
  { code: "376", iso: "AD", short: "AND", name: "Andorra", lengths: [6, 9] },
  { code: "244", iso: "AO", short: "AGO", name: "Angola", lengths: [9] },
  { code: "54", iso: "AR", short: "ARG", name: "Argentina", lengths: [10, 11] },
  { code: "374", iso: "AM", short: "ARM", name: "Armenia", lengths: [8] },
  { code: "297", iso: "AW", short: "ABW", name: "Aruba", lengths: [7] },
  { code: "247", iso: "AC", short: "AC", name: "Ascension Island", lengths: [5] },
  { code: "61", iso: "AU", short: "AUS", name: "Australia", lengths: [9] },
  { code: "43", iso: "AT", short: "AUT", name: "Austria", lengths: [7, 8, 9, 10, 11, 12, 13] },
  { code: "994", iso: "AZ", short: "AZE", name: "Azerbaijan", lengths: [9] },
  { code: "973", iso: "BH", short: "BHR", name: "Bahrain", lengths: [8] },
  { code: "880", iso: "BD", short: "BGD", name: "Bangladesh", lengths: [10] },
  { code: "375", iso: "BY", short: "BLR", name: "Belarus", lengths: [9] },
  { code: "32", iso: "BE", short: "BEL", name: "Belgium", lengths: [9] },
  { code: "501", iso: "BZ", short: "BLZ", name: "Belize", lengths: [7] },
  { code: "229", iso: "BJ", short: "BEN", name: "Benin", lengths: [8, 10] },
  { code: "975", iso: "BT", short: "BTN", name: "Bhutan", lengths: [8] },
  { code: "591", iso: "BO", short: "BOL", name: "Bolivia", lengths: [8] },
  { code: "387", iso: "BA", short: "BIH", name: "Bosnia & Herzegovina", lengths: [8, 9] },
  { code: "267", iso: "BW", short: "BWA", name: "Botswana", lengths: [8] },
  { code: "55", iso: "BR", short: "BRA", name: "Brazil", lengths: [10, 11] },
  { code: "246", iso: "IO", short: "IOT", name: "British Indian Ocean Territory", lengths: [7] },
  { code: "673", iso: "BN", short: "BRN", name: "Brunei", lengths: [7] },
  { code: "359", iso: "BG", short: "BGR", name: "Bulgaria", lengths: [8, 9] },
  { code: "226", iso: "BF", short: "BFA", name: "Burkina Faso", lengths: [8] },
  { code: "257", iso: "BI", short: "BDI", name: "Burundi", lengths: [8] },
  { code: "855", iso: "KH", short: "KHM", name: "Cambodia", lengths: [8, 9] },
  { code: "237", iso: "CM", short: "CMR", name: "Cameroon", lengths: [9] },
  { code: "238", iso: "CV", short: "CPV", name: "Cape Verde", lengths: [7] },
  { code: "236", iso: "CF", short: "CAF", name: "Central African Republic", lengths: [8] },
  { code: "235", iso: "TD", short: "TCD", name: "Chad", lengths: [8] },
  { code: "56", iso: "CL", short: "CHL", name: "Chile", lengths: [9] },
  { code: "86", iso: "CN", short: "CHN", name: "China", lengths: [11] },
  { code: "57", iso: "CO", short: "COL", name: "Colombia", lengths: [10] },
  { code: "269", iso: "KM", short: "COM", name: "Comoros", lengths: [7] },
  { code: "242", iso: "CG", short: "COG", name: "Congo - Brazzaville", lengths: [9] },
  { code: "243", iso: "CD", short: "COD", name: "Congo - Kinshasa", lengths: [7, 9] },
  { code: "682", iso: "CK", short: "COK", name: "Cook Islands", lengths: [5] },
  { code: "506", iso: "CR", short: "CRI", name: "Costa Rica", lengths: [8] },
  { code: "225", iso: "CI", short: "CIV", name: "Côte d’Ivoire", lengths: [10] },
  { code: "385", iso: "HR", short: "HRV", name: "Croatia", lengths: [8, 9] },
  { code: "53", iso: "CU", short: "CUB", name: "Cuba", lengths: [8] },
  { code: "599", iso: "CW", short: "CUW", name: "Curaçao", lengths: [7, 8] },
  { code: "357", iso: "CY", short: "CYP", name: "Cyprus", lengths: [8] },
  { code: "420", iso: "CZ", short: "CZE", name: "Czechia", lengths: [9] },
  { code: "45", iso: "DK", short: "DNK", name: "Denmark", lengths: [8] },
  { code: "253", iso: "DJ", short: "DJI", name: "Djibouti", lengths: [8] },
  { code: "593", iso: "EC", short: "ECU", name: "Ecuador", lengths: [9] },
  { code: "20", iso: "EG", short: "EGY", name: "Egypt", lengths: [10] },
  { code: "503", iso: "SV", short: "SLV", name: "El Salvador", lengths: [8] },
  { code: "240", iso: "GQ", short: "GNQ", name: "Equatorial Guinea", lengths: [9] },
  { code: "291", iso: "ER", short: "ERI", name: "Eritrea", lengths: [7] },
  { code: "372", iso: "EE", short: "EST", name: "Estonia", lengths: [7, 8] },
  { code: "268", iso: "SZ", short: "SWZ", name: "Eswatini", lengths: [8] },
  { code: "251", iso: "ET", short: "ETH", name: "Ethiopia", lengths: [9] },
  { code: "500", iso: "FK", short: "FLK", name: "Falkland Islands", lengths: [5] },
  { code: "298", iso: "FO", short: "FRO", name: "Faroe Islands", lengths: [6] },
  { code: "679", iso: "FJ", short: "FJI", name: "Fiji", lengths: [7] },
  { code: "358", iso: "FI", short: "FIN", name: "Finland", lengths: [6, 7, 8, 9, 10] },
  { code: "33", iso: "FR", short: "FRA", name: "France", lengths: [9] },
  { code: "594", iso: "GF", short: "GUF", name: "French Guiana", lengths: [9] },
  { code: "689", iso: "PF", short: "PYF", name: "French Polynesia", lengths: [8] },
  { code: "241", iso: "GA", short: "GAB", name: "Gabon", lengths: [7, 8] },
  { code: "220", iso: "GM", short: "GMB", name: "Gambia", lengths: [7] },
  { code: "995", iso: "GE", short: "GEO", name: "Georgia", lengths: [9] },
  { code: "49", iso: "DE", short: "DEU", name: "Germany", lengths: [10, 11] },
  { code: "233", iso: "GH", short: "GHA", name: "Ghana", lengths: [9] },
  { code: "350", iso: "GI", short: "GIB", name: "Gibraltar", lengths: [8] },
  { code: "30", iso: "GR", short: "GRC", name: "Greece", lengths: [10] },
  { code: "299", iso: "GL", short: "GRL", name: "Greenland", lengths: [6] },
  { code: "590", iso: "GP", short: "GLP", name: "Guadeloupe", lengths: [9] },
  { code: "502", iso: "GT", short: "GTM", name: "Guatemala", lengths: [8] },
  { code: "224", iso: "GN", short: "GIN", name: "Guinea", lengths: [9] },
  { code: "245", iso: "GW", short: "GNB", name: "Guinea-Bissau", lengths: [9] },
  { code: "592", iso: "GY", short: "GUY", name: "Guyana", lengths: [7] },
  { code: "509", iso: "HT", short: "HTI", name: "Haiti", lengths: [8] },
  { code: "504", iso: "HN", short: "HND", name: "Honduras", lengths: [8] },
  { code: "852", iso: "HK", short: "HKG", name: "Hong Kong SAR China", lengths: [8] },
  { code: "36", iso: "HU", short: "HUN", name: "Hungary", lengths: [9] },
  { code: "354", iso: "IS", short: "ISL", name: "Iceland", lengths: [7, 9] },
  { code: "91", iso: "IN", short: "IND", name: "India", lengths: [10] },
  { code: "62", iso: "ID", short: "IDN", name: "Indonesia", lengths: [9, 10, 11, 12] },
  { code: "98", iso: "IR", short: "IRN", name: "Iran", lengths: [10] },
  { code: "964", iso: "IQ", short: "IRQ", name: "Iraq", lengths: [10] },
  { code: "353", iso: "IE", short: "IRL", name: "Ireland", lengths: [9] },
  { code: "972", iso: "IL", short: "ISR", name: "Israel", lengths: [9] },
  { code: "39", iso: "IT", short: "ITA", name: "Italy", lengths: [9, 10] },
  { code: "81", iso: "JP", short: "JPN", name: "Japan", lengths: [10] },
  { code: "962", iso: "JO", short: "JOR", name: "Jordan", lengths: [9] },
  { code: "254", iso: "KE", short: "KEN", name: "Kenya", lengths: [9] },
  { code: "686", iso: "KI", short: "KIR", name: "Kiribati", lengths: [8] },
  { code: "383", iso: "XK", short: "XKK", name: "Kosovo", lengths: [8] },
  { code: "965", iso: "KW", short: "KWT", name: "Kuwait", lengths: [8] },
  { code: "996", iso: "KG", short: "KGZ", name: "Kyrgyzstan", lengths: [9] },
  { code: "856", iso: "LA", short: "LAO", name: "Laos", lengths: [10] },
  { code: "371", iso: "LV", short: "LVA", name: "Latvia", lengths: [8] },
  { code: "961", iso: "LB", short: "LBN", name: "Lebanon", lengths: [7, 8] },
  { code: "266", iso: "LS", short: "LSO", name: "Lesotho", lengths: [8] },
  { code: "231", iso: "LR", short: "LBR", name: "Liberia", lengths: [7, 9] },
  { code: "218", iso: "LY", short: "LBY", name: "Libya", lengths: [9] },
  { code: "423", iso: "LI", short: "LIE", name: "Liechtenstein", lengths: [7, 9] },
  { code: "370", iso: "LT", short: "LTU", name: "Lithuania", lengths: [8] },
  { code: "352", iso: "LU", short: "LUX", name: "Luxembourg", lengths: [9] },
  { code: "853", iso: "MO", short: "MAC", name: "Macao SAR China", lengths: [8] },
  { code: "261", iso: "MG", short: "MDG", name: "Madagascar", lengths: [9] },
  { code: "265", iso: "MW", short: "MWI", name: "Malawi", lengths: [9] },
  { code: "60", iso: "MY", short: "MYS", name: "Malaysia", lengths: [9, 10] },
  { code: "960", iso: "MV", short: "MDV", name: "Maldives", lengths: [7] },
  { code: "223", iso: "ML", short: "MLI", name: "Mali", lengths: [8] },
  { code: "356", iso: "MT", short: "MLT", name: "Malta", lengths: [8] },
  { code: "692", iso: "MH", short: "MHL", name: "Marshall Islands", lengths: [7] },
  { code: "596", iso: "MQ", short: "MTQ", name: "Martinique", lengths: [9] },
  { code: "222", iso: "MR", short: "MRT", name: "Mauritania", lengths: [8] },
  { code: "230", iso: "MU", short: "MUS", name: "Mauritius", lengths: [8] },
  { code: "52", iso: "MX", short: "MEX", name: "Mexico", lengths: [10] },
  { code: "691", iso: "FM", short: "FSM", name: "Micronesia", lengths: [7] },
  { code: "373", iso: "MD", short: "MDA", name: "Moldova", lengths: [8] },
  { code: "377", iso: "MC", short: "MCO", name: "Monaco", lengths: [8, 9] },
  { code: "976", iso: "MN", short: "MNG", name: "Mongolia", lengths: [8] },
  { code: "382", iso: "ME", short: "MNE", name: "Montenegro", lengths: [8] },
  { code: "212", iso: "MA", short: "MAR", name: "Morocco", lengths: [9] },
  { code: "258", iso: "MZ", short: "MOZ", name: "Mozambique", lengths: [9] },
  { code: "95", iso: "MM", short: "MMR", name: "Myanmar (Burma)", lengths: [7, 8, 9, 10] },
  { code: "264", iso: "NA", short: "NAM", name: "Namibia", lengths: [9] },
  { code: "674", iso: "NR", short: "NRU", name: "Nauru", lengths: [7] },
  { code: "977", iso: "NP", short: "NPL", name: "Nepal", lengths: [10] },
  { code: "31", iso: "NL", short: "NLD", name: "Netherlands", lengths: [9, 11] },
  { code: "687", iso: "NC", short: "NCL", name: "New Caledonia", lengths: [6] },
  { code: "64", iso: "NZ", short: "NZL", name: "New Zealand", lengths: [8, 9, 10] },
  { code: "505", iso: "NI", short: "NIC", name: "Nicaragua", lengths: [8] },
  { code: "227", iso: "NE", short: "NER", name: "Niger", lengths: [8] },
  { code: "234", iso: "NG", short: "NGA", name: "Nigeria", lengths: [10] },
  { code: "683", iso: "NU", short: "NIU", name: "Niue", lengths: [4, 7] },
  { code: "672", iso: "NF", short: "NFK", name: "Norfolk Island", lengths: [6] },
  { code: "850", iso: "KP", short: "PRK", name: "North Korea", lengths: [10] },
  { code: "389", iso: "MK", short: "MKD", name: "North Macedonia", lengths: [8] },
  { code: "47", iso: "NO", short: "NOR", name: "Norway", lengths: [8] },
  { code: "968", iso: "OM", short: "OMN", name: "Oman", lengths: [8] },
  { code: "92", iso: "PK", short: "PAK", name: "Pakistan", lengths: [10] },
  { code: "680", iso: "PW", short: "PLW", name: "Palau", lengths: [7] },
  { code: "970", iso: "PS", short: "PSE", name: "Palestinian Territories", lengths: [9] },
  { code: "507", iso: "PA", short: "PAN", name: "Panama", lengths: [7, 8] },
  { code: "675", iso: "PG", short: "PNG", name: "Papua New Guinea", lengths: [8] },
  { code: "595", iso: "PY", short: "PRY", name: "Paraguay", lengths: [9] },
  { code: "51", iso: "PE", short: "PER", name: "Peru", lengths: [9] },
  { code: "63", iso: "PH", short: "PHL", name: "Philippines", lengths: [10] },
  { code: "48", iso: "PL", short: "POL", name: "Poland", lengths: [9] },
  { code: "351", iso: "PT", short: "PRT", name: "Portugal", lengths: [9] },
  { code: "974", iso: "QA", short: "QAT", name: "Qatar", lengths: [8] },
  { code: "262", iso: "RE", short: "REU", name: "Réunion", lengths: [9] },
  { code: "40", iso: "RO", short: "ROU", name: "Romania", lengths: [9] },
  { code: "7", iso: "RU", short: "RUS", name: "Russia / Kazakhstan", lengths: [10] },
  { code: "250", iso: "RW", short: "RWA", name: "Rwanda", lengths: [9] },
  { code: "685", iso: "WS", short: "WSM", name: "Samoa", lengths: [7, 10] },
  { code: "378", iso: "SM", short: "SMR", name: "San Marino", lengths: [8] },
  { code: "239", iso: "ST", short: "STP", name: "São Tomé & Príncipe", lengths: [7] },
  { code: "966", iso: "SA", short: "SAU", name: "Saudi Arabia", lengths: [9] },
  { code: "221", iso: "SN", short: "SEN", name: "Senegal", lengths: [9] },
  { code: "381", iso: "RS", short: "SRB", name: "Serbia", lengths: [8, 9, 10] },
  { code: "248", iso: "SC", short: "SYC", name: "Seychelles", lengths: [7] },
  { code: "232", iso: "SL", short: "SLE", name: "Sierra Leone", lengths: [8] },
  { code: "65", iso: "SG", short: "SGP", name: "Singapore", lengths: [8] },
  { code: "421", iso: "SK", short: "SVK", name: "Slovakia", lengths: [9] },
  { code: "386", iso: "SI", short: "SVN", name: "Slovenia", lengths: [8] },
  { code: "677", iso: "SB", short: "SLB", name: "Solomon Islands", lengths: [5, 7] },
  { code: "252", iso: "SO", short: "SOM", name: "Somalia", lengths: [7, 8, 9] },
  { code: "27", iso: "ZA", short: "ZAF", name: "South Africa", lengths: [5, 6, 7, 8, 9] },
  { code: "82", iso: "KR", short: "KOR", name: "South Korea", lengths: [9, 10] },
  { code: "211", iso: "SS", short: "SSD", name: "South Sudan", lengths: [9] },
  { code: "34", iso: "ES", short: "ESP", name: "Spain", lengths: [9] },
  { code: "94", iso: "LK", short: "LKA", name: "Sri Lanka", lengths: [9] },
  { code: "290", iso: "SH", short: "SHN", name: "St. Helena", lengths: [5] },
  { code: "508", iso: "PM", short: "SPM", name: "St. Pierre & Miquelon", lengths: [6, 9] },
  { code: "249", iso: "SD", short: "SDN", name: "Sudan", lengths: [9] },
  { code: "597", iso: "SR", short: "SUR", name: "Suriname", lengths: [7] },
  { code: "46", iso: "SE", short: "SWE", name: "Sweden", lengths: [9] },
  { code: "41", iso: "CH", short: "CHE", name: "Switzerland", lengths: [9] },
  { code: "963", iso: "SY", short: "SYR", name: "Syria", lengths: [9] },
  { code: "886", iso: "TW", short: "TWN", name: "Taiwan", lengths: [9] },
  { code: "992", iso: "TJ", short: "TJK", name: "Tajikistan", lengths: [9] },
  { code: "255", iso: "TZ", short: "TZA", name: "Tanzania", lengths: [9] },
  { code: "66", iso: "TH", short: "THA", name: "Thailand", lengths: [9] },
  { code: "670", iso: "TL", short: "TLS", name: "Timor-Leste", lengths: [8] },
  { code: "228", iso: "TG", short: "TGO", name: "Togo", lengths: [8] },
  { code: "690", iso: "TK", short: "TKL", name: "Tokelau", lengths: [4, 5, 6, 7] },
  { code: "676", iso: "TO", short: "TON", name: "Tonga", lengths: [7] },
  { code: "216", iso: "TN", short: "TUN", name: "Tunisia", lengths: [8] },
  { code: "90", iso: "TR", short: "TUR", name: "Türkiye", lengths: [10] },
  { code: "993", iso: "TM", short: "TKM", name: "Turkmenistan", lengths: [8] },
  { code: "688", iso: "TV", short: "TUV", name: "Tuvalu", lengths: [6, 7] },
  { code: "256", iso: "UG", short: "UGA", name: "Uganda", lengths: [9] },
  { code: "380", iso: "UA", short: "UKR", name: "Ukraine", lengths: [9] },
  { code: "971", iso: "AE", short: "UAE", name: "United Arab Emirates", lengths: [9] },
  { code: "44", iso: "GB", short: "UK", name: "United Kingdom", lengths: [10] },
  { code: "598", iso: "UY", short: "URY", name: "Uruguay", lengths: [8] },
  { code: "1", iso: "US", short: "USA", name: "USA / Canada", lengths: [10] },
  { code: "998", iso: "UZ", short: "UZB", name: "Uzbekistan", lengths: [9] },
  { code: "678", iso: "VU", short: "VUT", name: "Vanuatu", lengths: [7] },
  { code: "58", iso: "VE", short: "VEN", name: "Venezuela", lengths: [10] },
  { code: "84", iso: "VN", short: "VNM", name: "Vietnam", lengths: [9] },
  { code: "681", iso: "WF", short: "WLF", name: "Wallis & Futuna", lengths: [6] },
  { code: "967", iso: "YE", short: "YEM", name: "Yemen", lengths: [9] },
  { code: "260", iso: "ZM", short: "ZMB", name: "Zambia", lengths: [9] },
  { code: "263", iso: "ZW", short: "ZWE", name: "Zimbabwe", lengths: [9] },
];

const BY_CODE = new Map(COUNTRY_CODES.map((c) => [c.code, c]));

export const countryByCode = (code: string): CountryCode | undefined => BY_CODE.get(code);

// Valid national lengths for a code; 10 when the code is unknown (old behaviour).
export const nationalLengths = (code: string): number[] => BY_CODE.get(code)?.lengths || [10];

export const minNationalLength = (code: string): number => Math.min(...nationalLengths(code));
export const maxNationalLength = (code: string): number => Math.max(...nationalLengths(code));

export const isValidNationalLength = (code: string, national: string): boolean =>
  nationalLengths(code).includes(national.length);

// What may go in the phone box for a code: digits only, a leading 0 dropped (the
// local trunk prefix people type out of habit: Australia 0412 -> 412, UK 07 -> 7;
// Italy keeps it, its 0 is part of the number), cut to the code's longest length.
export const cleanNationalInput = (code: string, raw: string): string => {
  let d = String(raw || "").replace(/\D/g, "");
  if (code !== "39") d = d.replace(/^0+/, "");
  return d.slice(0, maxNationalLength(code));
};

// The hospital's list, cleaned: known codes only, in the stored order. Falls back
// to the default four when the list is missing or empty.
export const allowedCountryCodes = (fromFlags?: string[] | null): string[] => {
  const list = (Array.isArray(fromFlags) ? fromFlags : [])
    .map((c) => String(c).replace(/^\+/, "").trim())
    .filter((c) => BY_CODE.has(c));
  return list.length ? Array.from(new Set(list)) : DEFAULT_COUNTRY_CODES;
};

// Split a stored full number ("916362850197") into code + national part.
// Codes overlap (+1 vs +1242, +7 vs +77), so the LONGEST matching code wins,
// and the rest must be a valid length for that code.
export const splitPhone = (
  digits: string,
  codes: string[] = COUNTRY_CODES.map((c) => c.code)
): { code: string; national: string } | null => {
  const clean = String(digits || "").replace(/\D/g, "");
  const sorted = [...codes].sort((a, b) => b.length - a.length);
  for (const code of sorted) {
    if (clean.startsWith(code) && isValidNationalLength(code, clean.slice(code.length))) {
      return { code, national: clean.slice(code.length) };
    }
  }
  return null;
};
